import { clientTransformed } from '@common/utils/rest';
import { mapToObj } from '@common/utils/map';
import { CONTEXT_TYPE } from '../constants/main';

const getUrl = (path) => {
  return `api/fieldAudit/${path}`;
};

export const getReport = ({ 
  pagination, 
  sorting, 
  dateStart, 
  dateEnd, 
  users,
  indFlag,
  pedFlag,
  indItemId,
  indItemSystem,
  pedItemId,
  pedItemSystem,
}) => {
  const sortAsc = (sorting.direction || 'asc').toUpperCase();

  return clientTransformed({
    path: getUrl('fieldAuditReport'),
    method: 'POST',
    entity: {
      ...pagination,
      searchQuery: '',
      sortColIndex: sorting.columnIndex || 1,
      sortAsc,
      dateStart: dateStart || null,
      dateEnd: dateEnd || null,
      users: users || [],
      indFlag, 
      pedFlag,
      indItemId,
      indItemSystem,
      pedItemId,
      pedItemSystem,
      indFields: [94, 115],
      pedFields: [],
    }
  }).then((response) => {
    return response.entity;
  });
};

export const getFilteringConfig = () => {
  return clientTransformed({
    path: getUrl('fieldAuditConf'),
    method: 'GET',
  }).then((response) => {
    return response.entity;
  });
};

export const getLastFilters = () => {
  return clientTransformed({
    path: getUrl('fieldAuditLastConf'),
    method: 'GET',
  }).then((response) => {
    return response.entity;
  });
};

export const setColumnsWidth = ({ name, value }) => {
  return clientTransformed({
    path: getUrl('fieldAuditUpdateWidth'),
    params: {
      value,
      key: name,
    },
    method: 'POST',
  }).then((response) => {
    return response.entity;
  });
};

export const exportData = (data) => {
  const path = getUrl('exportFieldAuditReport');

  return fetch(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  }).then(async (x) => {
    const blob = await x.blob();

    return {
      headers: mapToObj(x.headers),
      entity: blob,
    };
  });
};

const createRootFolder = ({ id, name, children, imageURL, system }) => {
  return {
    id, 
    name,
    children,
    imageURL,
    system,
    imageURLSelected: imageURL,
  };
};

export const getFolders = ({ context }) => {
  return clientTransformed({
    path: getUrl('fieldAuditFoldersConf'),
    params: {
      folderId: 0,
      context,
    },
    method: 'GET',
  }).then((response) => {
    const foldersByType = response.entity.folders.reduce((acc, folder) => {
      if (folder.system) {
        folder.imageURL = 'gif/system.gif';
        folder.imageURLSelected = 'gif/system.gif';

        acc.system.push(folder);
      } else {
        acc.custom.push(folder);
      }

      return acc;
    }, { custom: [], system: [] });

    const config = {
      [CONTEXT_TYPE.individual]: {
        name: 'Individual Data Folders',
      },
      [CONTEXT_TYPE.pedigree]: {
        name: 'Pedigree Data Folders',
      },
    };

    const ROOT_SYSTEM_FIELDS_ID = 0;

    const systemRootFolder = createRootFolder({
      id: ROOT_SYSTEM_FIELDS_ID,
      name: 'System Fields',
      children: foldersByType.system,
      imageURL: 'gif/system.gif',
      system: true,
    });
    const customRootFolder = createRootFolder({
      id: '',
      name: config[context].name,
      children: foldersByType.custom,
      imageURL: 'gif/df_individual.gif',
      system: false,
    });

    return [
      systemRootFolder,
      customRootFolder
    ];
  });
};

export const getAuditingFields = ({ folderId, context }) => {
  return clientTransformed({
    path: getUrl('fieldAuditFoldersConf'),
    params: {
      folderId,
      context,
    },
    method: 'GET',
  }).then((response) => {
    return  response.entity.fields.map(({ id, name, system, ...otherData }) => ({ ...otherData, fieldName: name, fieldId: id, systemField: system }));
  });
};

export const ENTITY_TYPE_TYPE = {
  folder: 'folder',
  field: 'field'
};

export const getFields = () => {
  return clientTransformed({
    path: getUrl('editFieldAudit'),
    method: 'GET',
  }).then(({ entity: { individuals, pedigrees } }) => {
    const processFolder = ({ folderId, folderName, children, fields }) => {
      return {
        id: `folder-${folderId}`, 
        entityId: folderId,
        name: folderName,
        type: ENTITY_TYPE_TYPE.folder,
        children: [
          ...(children || []).map(processFolder),
          ...fields.map((x) => ({
            entityId: x.fieldId,
            type: ENTITY_TYPE_TYPE.field,
            auditOn: x.auditOn,
            context: x.context,
            dataFolderId: x.dataFolderId,
            id: `field-${folderId}-${x.fieldId}`,
            name: x.fieldName,
            imageURL: x.imageURL,
            imageURLSelected: x.imageURL,
            children: [],
          }))
        ],
      };
    };

    individuals = createRootFolder({
      id: 'individual-folder',
      name: 'Individual Data Folders',
      children: individuals.map(processFolder),
      imageURL: 'gif/df_individual.gif',
    });
    pedigrees = createRootFolder({
      id: 'pedigree-folder',
      name: 'Pedigree Data Folders',
      children: pedigrees.map(processFolder),
      imageURL: 'gif/df_individual.gif',
    });

    return [
      individuals,
      pedigrees
    ];
  });
};

export const updateFieldsToAudit = (fields) => {
  return clientTransformed({
    path: getUrl('updateFieldAudit'),
    entity: fields,
    method: 'POST',
  });
};